import { useState } from 'react';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import type { CannedResponse } from '../../lib/types';
import { loadCanned } from '../Canned';
import { Button } from '../ui';
import { AddButton, Cell, ConfirmButton, Drawer, Field, inputClass, Pill, Row, SectionHeader, Table, useLoad } from './kit';

const CHANNEL: Record<CannedResponse['channel'], string> = { all: 'Chat and email', chat: 'Chat', email: 'Email' };

/**
 * Canned responses: ready answers agents insert while chatting or replying
 * to email, from the button by the reply box or, in a chat, by typing "/"
 * and the shortcut.
 */
export function Canned() {
  const [list, reload] = useLoad<CannedResponse[]>('/canned-responses');
  const [editing, setEditing] = useState<CannedResponse | 'new' | null>(null);
  return (
    <>
      <SectionHeader
        title="Canned responses"
        lead="Ready answers for chats and emails. Agents pick them by the reply box, or type / and the shortcut in a chat. {customer_first_name}, {customer_name}, {agent_first_name} and {agent_name} are filled in."
        action={<AddButton onClick={() => setEditing('new')}>New response</AddButton>}
      />
      <Table head={['Title', 'Shortcut', 'Used in', 'Text']} empty={list?.length === 0 && 'No canned responses yet.'}>
        {(list ?? []).map((c) => (
          <Row key={c.id} onClick={() => setEditing(c)}>
            <Cell>
              <span className="font-medium">{c.title}</span>
            </Cell>
            <Cell className="tabular text-ink-2">{c.shortcut ? `/${c.shortcut}` : ''}</Cell>
            <Cell>
              <Pill>{CHANNEL[c.channel]}</Pill>
            </Cell>
            <Cell className="max-w-[360px] truncate text-ink-2">{c.body}</Cell>
          </Row>
        ))}
      </Table>
      {editing ? (
        <CannedDrawer
          item={editing === 'new' ? null : editing}
          onDone={() => {
            setEditing(null);
            reload();
            void loadCanned(true);
          }}
        />
      ) : null}
    </>
  );
}

function CannedDrawer({ item, onDone }: { item: CannedResponse | null; onDone: () => void }) {
  const [f, setF] = useState({ title: item?.title ?? '', shortcut: item?.shortcut ?? '', body: item?.body ?? '', channel: item?.channel ?? ('all' as CannedResponse['channel']) });
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  async function save() {
    setBusy(true);
    const body = { ...f, shortcut: f.shortcut.trim().replace(/^\//, '') || null };
    const ok = await act(() => (item ? api('PATCH', `/canned-responses/${item.id}`, body) : api('POST', '/canned-responses', body)), item ? 'Saved' : 'Added');
    setBusy(false);
    if (ok) onDone();
  }
  async function remove() {
    if (await act(() => api('DELETE', `/canned-responses/${item!.id}`).then(() => true), 'Deleted')) onDone();
  }
  return (
    <Drawer
      title={item ? item.title : 'New canned response'}
      open
      onClose={onDone}
      footer={
        <>
          <Button tone="primary" className="px-4 py-2" disabled={busy || f.title.trim().length < 2 || !f.body.trim()} onClick={() => void save()}>
            {item ? 'Save' : 'Add'}
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onDone}>
            Cancel
          </Button>
          {item ? (
            <span className="ml-auto">
              <ConfirmButton confirmText="Delete it" onConfirm={() => void remove()}>
                Delete
              </ConfirmButton>
            </span>
          ) : null}
        </>
      }
    >
      <Field label="Title">{(id) => <input id={id} className={inputClass} value={f.title} maxLength={80} onChange={(e) => set('title', e.target.value)} placeholder="Delivery times" />}</Field>
      <Field label="Shortcut" hint="Optional. In a chat, typing / and this offers the response. Letters, digits, - and _.">
        {(id) => <input id={id} className={`${inputClass} tabular`} value={f.shortcut} maxLength={31} onChange={(e) => set('shortcut', e.target.value.toLowerCase())} placeholder="delivery" />}
      </Field>
      <Field label="Used in">
        {(id) => (
          <select id={id} className={inputClass} value={f.channel} onChange={(e) => set('channel', e.target.value as CannedResponse['channel'])}>
            <option value="all">Chat and email</option>
            <option value="chat">Chat only</option>
            <option value="email">Email only</option>
          </select>
        )}
      </Field>
      <Field label="Text" hint="{customer_first_name}, {customer_name}, {agent_first_name} and {agent_name} are filled in.">
        {(id) => <textarea id={id} rows={8} className={inputClass} value={f.body} maxLength={5000} onChange={(e) => set('body', e.target.value)} placeholder="Hi {customer_first_name}, deliveries take two to three working days." />}
      </Field>
    </Drawer>
  );
}
